import React from 'react';
import classnames from 'classnames';
import { TableRow, Tooltip } from '@getsynapse/design-system';
import { useSelector } from 'react-redux';
import { RequestsDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import get from 'lodash/get';
import UserAvatar from '../../../../Atoms/UserAvatar';
import RequestLink from '../../../../Atoms/RequestLink';
import { getUserAvartarColumn } from '../../helpers';
import capitalize from 'lodash/capitalize';
import RequestStatusTag from '../../../../Atoms/RequestStatusTag/RequestStatusTag';
import moment from 'moment';
import { DATE } from 'utils/constants';
import { getRequestRoute } from 'utils/dynamicRoutes';
import { selectOrganizationId } from 'state/User/userSlice';

type RequestDrillDownTableRowProps = {
  item: RequestsDrillDownItem;
  readOnly: boolean;
};

const RequestDrillDownTableRow: React.FC<RequestDrillDownTableRowProps> = ({
  item,
  readOnly,
}) => {
  const organizationId = useSelector(selectOrganizationId);
  const requestHref = getRequestRoute(item.id, organizationId, {
    isJsonSchema: item.isJsonSchema,
  });

  return (
    <TableRow
      id={item.id}
      className='group cursor-pointer'
      cells={[
        {
          content: (
            <>
              {readOnly ? (
                item.request_number
              ) : (
                <RequestLink
                  href={requestHref}
                  isJsonSchema={item.isJsonSchema}
                  className='text-steel-blue underline'
                >
                  {item.request_number}
                </RequestLink>
              )}
            </>
          ),
        },
        { content: item.title },
        {
          content: (
            <div className={classnames({ 'w-36': !!item.id })}>
              <RequestStatusTag status={item.request_status} />
            </div>
          ),
        },
        { content: item.request_type },
        {
          content: item.requester && (
            <div className='flex items-center gap-1'>
              <UserAvatar
                size='small'
                user={{
                  avatar_url: get(item, 'requester.avatar_url'),
                  status: get(item, 'request_status'),
                  data: {
                    firstName: get(item, 'requester.first_name'),
                    lastName: get(item, 'requester.last_name'),
                    email: get(item, 'requester.email'),
                  },
                }}
              />
              <span className='whitespace-nowrap'>
                {`${get(item, 'requester.first_name')} ${get(
                  item,
                  'requester.last_name'
                )}`}
              </span>
            </div>
          ),
        },
        {
          content:
            item.owners?.length > 0 &&
            getUserAvartarColumn(
              item.owners.map((i) => ({
                avatar_url: get(i, 'avatar_url') || undefined,
                status: get(i, 'status'),
                data: {
                  firstName: get(i, 'first_name'),
                  lastName: get(i, 'last_name'),
                  email: get(i, 'email'),
                },
              }))
            ),
        },
        {
          content: (
            <Tooltip
              openMode='hover1'
              position='topCenter'
              contentProps={{
                className: classnames(
                  'bg-neutral-darker shadow-tooltip',
                  'rounded-lg px-4 py-3',
                  'absolute',
                  'w-max whitespace-normal',
                  'text-body',
                  'z-50'
                ),
              }}
              timeout={0}
              showPopper
              className={`w-full truncate`}
              trigger={<span>{item.projects?.length || 0}</span>}
            >
              {(item.projects || []).map((i) => i.title).join(', ')}
            </Tooltip>
          ),
        },
        { content: item.business_unit_title },
        { content: capitalize(item.ld_priority) },
        { content: item.form_title },
        {
          content: item.creation_date && (
            <div>
              {moment(new Date(item.creation_date)).format(DATE.SHORT_FORMAT)}
            </div>
          ),
        },
        {
          content: item.submission_date && (
            <div>
              {moment(new Date(item.submission_date)).format(DATE.SHORT_FORMAT)}
            </div>
          ),
        },
        {
          content: item.decision_date && (
            <div>
              {moment(new Date(item.decision_date)).format(DATE.SHORT_FORMAT)}
            </div>
          ),
        },
        {
          content: item.last_update_date && (
            <div>
              {moment(new Date(item.last_update_date)).format(
                DATE.SHORT_FORMAT
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default RequestDrillDownTableRow;
